import { useState, useEffect, useRef } from "react";
import MatchHorizontalCard from "./MatchHorizontalCard";
import { Button } from "@/components/ui/button";
import { RotateCcw, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface Match {
  user_id: string;
  display_name: string | null;
  photo_urls: string[] | null;
  common_count: number;
  match_tier: string;
  bio?: string;
}

interface MatchHorizontalViewProps {
  matches: Match[];
  isLoading: boolean;
  onRefresh: () => void;
  onApprove: (matchId: string) => void;
  onReject: (matchId: string) => void;
}

const MatchHorizontalView = ({
  matches,
  isLoading,
  onRefresh,
  onApprove,
  onReject
}: MatchHorizontalViewProps) => {
  const [actionHistory, setActionHistory] = useState<{matchId: string, action: 'approve' | 'reject'}[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        scrollLeft();
      } else if (e.key === 'ArrowRight') {
        scrollRight();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -320,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 320,
        behavior: 'smooth'
      });
    }
  };

  const handleApprove = (matchId: string) => {
    onApprove(matchId);
    setActionHistory(prev => [...prev, { matchId, action: 'approve' }]);
  };

  const handleReject = (matchId: string) => {
    onReject(matchId);
    setActionHistory(prev => [...prev, { matchId, action: 'reject' }]);
  };

  const handleUndo = () => {
    if (actionHistory.length === 0) return;
    
    const lastAction = actionHistory[actionHistory.length - 1];
    setActionHistory(prev => prev.slice(0, -1));
    
    // Note: In a real app, you'd want to reverse the action in the backend
    console.log(`Undoing ${lastAction.action} for ${lastAction.matchId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <AlphaLoader size="small" />
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <Zap className="w-16 h-16 text-ice-white/40 mb-4" />
        <h3 className="text-lg font-semibold text-ice-white mb-2">No Matches Found</h3>
        <p className="text-ice-white/60 mb-4">
          Add more private interests to find compatible matches
        </p>
        <Button onClick={onRefresh} variant="outline" className="bdsm-button">
          Refresh Matches
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Instructions */}
      <div className="text-center mb-6">
        <p className="text-sm text-ice-white/60">
          Scroll through matches • Use arrow keys or scroll horizontally
        </p>
      </div>

      {/* Navigation Controls */}
      <div className="flex justify-between items-center mb-4">
        <Button 
          onClick={scrollLeft}
          variant="outline" 
          size="sm"
          className="border-ice-white/30 text-ice-white hover:bg-ice-white/10"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        
        <div className="text-sm text-ice-white/60">
          {matches.length} matches available
        </div>
        
        <Button 
          onClick={scrollRight}
          variant="outline" 
          size="sm"
          className="border-ice-white/30 text-ice-white hover:bg-ice-white/10"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Horizontal Scroll Container */}
      <div 
        ref={scrollContainerRef}
        className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {matches.map((match) => (
          <div key={match.user_id} className="snap-center flex-shrink-0">
            <MatchHorizontalCard
              match={match}
              onApprove={() => handleApprove(match.user_id)}
              onReject={() => handleReject(match.user_id)}
            />
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex justify-center gap-4 mt-6">
        {actionHistory.length > 0 && (
          <Button 
            onClick={handleUndo} 
            variant="outline" 
            size="sm" 
            className="border-ice-white/30 text-ice-white hover:bg-ice-white/10"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Undo Last Action
          </Button>
        )}
        
        <Button onClick={onRefresh} className="bdsm-button">
          Refresh Matches
        </Button>
      </div>
    </div>
  );
};

export default MatchHorizontalView;